import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Send, Archive, RotateCcw, History, Target, BookOpen, CheckCircle2, FileCheck2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SopDiffDialog } from "@/components/sop/SopDiff";
import { RelatedForms } from "@/components/forms/RelatedForms";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_ICON, formatDate } from "@/lib/constants";
import { RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";
import { DeleteButton } from "@/components/DeleteButton";

const ActionBtn = ({ onClick, icon: Icon, children, testId, dark }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
      dark ? "bg-lasso-ink text-white hover:bg-lasso-navy" : "border border-lasso-line bg-white hover:border-lasso-ink"
    }`}
  >
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const Steps = ({ steps, prefix = "sop-step-item" }) => (
  <ol className="space-y-4">
    {steps.map((s, i) => (
      <li key={i} id={`step-${i + 1}`} data-testid={`${prefix}-${i + 1}`} className="scroll-mt-24 rounded-2xl border border-lasso-line bg-white p-6 sm:p-7">
        <div className="flex gap-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-lasso-ink font-display text-sm font-bold text-white">{i + 1}</span>
          <div className="min-w-0">
            <h3 className="font-display text-base font-bold leading-snug">{s.title}</h3>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-zinc-700">{s.detail}</p>
          </div>
        </div>
      </li>
    ))}
  </ol>
);

function SummaryEditor({ sopId, version, bullets, onSaved, onClose }) {
  const [text, setText] = useState("");
  useEffect(() => { if (version) setText((bullets || []).map((b) => `• ${b}`).join("\n")); }, [version, bullets]);
  const save = async () => {
    try {
      const lines = text.split("\n").map((l) => l.replace(/^[\s•\-*]+/, "").trim()).filter(Boolean);
      await api.put(`/sops/${sopId}/versions/${version}/summary`, { bullets: lines });
      toast.success(`Change summary for v${version} saved`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={!!version} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="summary-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit “What changed” for v{version}</DialogTitle></DialogHeader>
        <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="summary-edit-textarea" />
        <button onClick={save} className="mt-2 self-end rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="summary-edit-save">Save summary</button>
      </DialogContent>
    </Dialog>
  );
}

const Bullets = ({ items, testId }) => (
  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-relaxed text-zinc-600" data-testid={testId}>{items.map((b, i) => <li key={i}>{b}</li>)}</ul>
);

const LinkBtn = ({ onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className="text-xs font-semibold text-lasso-deep transition-colors hover:text-lasso-ink">{children}</button>
);

function VersionHistory({ sopId, currentVersion, onSummaryChange }) {
  const { user } = useAuth();
  const [versions, setVersions] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [compare, setCompare] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(`/sops/${sopId}/versions`).then((r) => setVersions(r.data)), [sopId]);

  useEffect(() => { load(); }, [load, currentVersion]);

  return (
    <section className="mt-16" data-testid="sop-version-history">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.14em]"><History className="h-4 w-4" /> Version history</h2>
      <div className="mt-5 divide-y divide-lasso-line rounded-2xl border border-lasso-line bg-white">
        <div className="flex items-center justify-between px-5 py-4 text-sm">
          <span><span className="font-mono font-semibold">v{currentVersion}</span> · Current version</span>
        </div>
        {versions?.map((v) => (
          <div key={v.id} data-testid={`sop-version-${v.version}`} className="px-5 py-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span><span className="font-mono font-semibold">v{v.version}</span> · Replaced {formatDate(v.archived_at)} by {v.changed_by}{v.change_note ? ` — “${v.change_note}”` : ""}</span>
              <span className="flex gap-4">
                <LinkBtn onClick={() => setViewing(v)} testId={`sop-version-${v.version}-view`}>View</LinkBtn>
                <LinkBtn onClick={() => setCompare([v.version, v.version + 1])} testId={`sop-version-${v.version}-compare`}>Compare with v{v.version + 1}</LinkBtn>
                {user.role === "admin" && <LinkBtn onClick={() => setEditing(v)} testId={`sop-version-${v.version}-edit-summary`}>Edit summary</LinkBtn>}
              </span>
            </div>
            {v.change_summary?.length > 0 && (
              <div className="mt-2 rounded-lg bg-lasso-paper px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-lasso-deep">Changes in v{v.version + 1}</p>
                <Bullets items={v.change_summary} testId={`sop-version-${v.version}-summary`} />
              </div>
            )}
          </div>
        ))}
        {versions?.length === 0 && <p className="px-5 py-4 text-sm text-muted-foreground">No previous versions.</p>}
      </div>
      <SopDiffDialog sopId={sopId} range={compare} onClose={() => setCompare(null)} />
      <SummaryEditor sopId={sopId} version={editing ? editing.version + 1 : null} bullets={editing?.change_summary} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); onSummaryChange(); }} />
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="sop-version-dialog">
          <DialogHeader><DialogTitle className="font-display">v{viewing?.version} · {viewing?.snapshot.title}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">{viewing.snapshot.summary}</p>
              <Steps steps={viewing.snapshot.steps} prefix="sop-version-step" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function AckCard({ sopId, version }) {
  const [ack, setAck] = useState(null);
  const load = useCallback(() => api.get(`/me/sop-acks/${sopId}`).then((r) => setAck(r.data)), [sopId]);
  useEffect(() => { load(); }, [load, version]);

  const acknowledge = async () => {
    try {
      await api.post(`/sops/${sopId}/acknowledge`);
      toast.success(`Acknowledged v${version}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!ack) return null;
  const current = ack.acknowledged && !ack.needs_reack;
  return (
    <div className={`mt-8 flex flex-col gap-4 rounded-2xl border p-6 sm:flex-row sm:items-center sm:justify-between ${current ? "border-emerald-200 bg-emerald-50" : ack.needs_reack ? "border-amber-200 bg-amber-50" : "border-lasso-ink bg-white"}`} data-testid="sop-ack-card">
      <div className="flex gap-3">
        {current ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" /> : <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-lasso-deep" />}
        <div>
          <p className="font-display text-sm font-bold" data-testid="sop-ack-status">
            {current ? `Acknowledged v${ack.acked_version}` : ack.needs_reack ? `Updated to v${ack.current_version} — please re-acknowledge` : "Read & acknowledge"}
          </p>
          <p className="mt-1 text-sm text-zinc-600">
            {current ? `You confirmed you read this SOP on ${formatDate(ack.acknowledged_at)}.` : ack.needs_reack ? `You acknowledged v${ack.acked_version} on ${formatDate(ack.acknowledged_at)}. This SOP has changed since then.` : "Confirm you have read and understand this procedure."}
            {ack.assigned && ack.due_date && !current && <span className={`ml-1 font-semibold ${ack.overdue ? "text-rose-600" : ""}`}>Due {formatDate(ack.due_date + "T12:00:00")}{ack.overdue ? " (overdue)" : ""}.</span>}
          </p>
        </div>
      </div>
      {!current && (
        <button onClick={acknowledge} className="shrink-0 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="sop-acknowledge-button">
          {ack.needs_reack ? "Re-acknowledge" : "I've read & understand this SOP"}
        </button>
      )}
    </div>
  );
}

export default function SopDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [sop, setSop] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get(`/sops/${id}`).then((r) => setSop(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);

  useEffect(load, [load]);

  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/sops/${id}/status`, { status });
      setSop(data);
      toast.success(`SOP ${status === "published" ? "published" : status === "archived" ? "archived" : "moved to draft"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) return <EmptyState title="SOP not available" description={error} testId="sop-detail-error" />;
  if (!sop) return null;
  const Icon = CATEGORY_ICON[sop.category] || BookOpen;

  return (
    <div className="fade-up">
      <Link to="/sops" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="sop-back-link">
        <ArrowLeft className="h-4 w-4" /> SOP Library
      </Link>

      <header className="mt-8 border-b border-lasso-line pb-10">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep"><Icon className="h-4 w-4" /> {sop.category}</span>
          {isAdmin && <StatusBadge status={sop.status} testId="sop-detail-status" />}
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl" data-testid="sop-detail-title">{sop.title}</h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-zinc-600 sm:text-lg">{sop.summary}</p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-zinc-500">
          <span className="font-mono font-semibold text-lasso-ink" data-testid="sop-detail-version">v{sop.version}</span>
          <span data-testid="sop-detail-updated">Last updated {formatDate(sop.updated_at)}{sop.updated_by ? ` by ${sop.updated_by}` : ""}</span>
          <div className="flex flex-wrap gap-1.5">{sop.applicable_roles.map((r) => <RoleBadge key={r} role={r} />)}</div>
        </div>
        {sop.can_edit && (
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to={`/sops/${id}/edit`} data-testid="sop-edit-button" className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-lasso-navy">
              <Pencil className="h-4 w-4" /> Edit
            </Link>
            {sop.status !== "published" && <ActionBtn onClick={() => setStatus("published")} icon={Send} testId="sop-publish-button">Publish</ActionBtn>}
            {sop.status !== "archived" && <ActionBtn onClick={() => setStatus("archived")} icon={Archive} testId="sop-archive-button">Archive</ActionBtn>}
            {sop.status === "archived" && <ActionBtn onClick={() => setStatus("draft")} icon={RotateCcw} testId="sop-restore-button">Restore to draft</ActionBtn>}
            {isAdmin && <DeleteButton path={`/sops/${id}`} name={sop.title} what="SOP" redirect="/sops" testId="sop-delete-button" />}
          </div>
        )}
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:block">
          <nav className="sticky top-10 space-y-1" data-testid="sop-step-index">
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-400">Steps</p>
            {sop.steps.map((s, i) => (
              <a key={i} href={`#step-${i + 1}`} className="flex gap-3 rounded-lg px-2 py-1.5 text-sm text-zinc-600 transition-colors hover:bg-white hover:text-lasso-ink">
                <span className="font-mono text-xs text-lasso-deep">{String(i + 1).padStart(2, "0")}</span>
                <span className="line-clamp-2">{s.title}</span>
              </a>
            ))}
          </nav>
        </aside>
        <div>
          {sop.version > 1 && sop.change_summary_version === sop.version && sop.change_summary?.length > 0 && (
            <div className="mb-8 rounded-2xl border border-lasso-sky bg-white p-6" data-testid="sop-whats-new">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep"><Sparkles className="h-4 w-4" /> What's new in v{sop.version}</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-zinc-700">{sop.change_summary.map((b, i) => <li key={i}>{b}</li>)}</ul>
            </div>
          )}
          {sop.purpose && (
            <div className="mb-8 flex gap-4 rounded-2xl bg-lasso-tint p-6" data-testid="sop-detail-purpose">
              <Target className="mt-0.5 h-5 w-5 shrink-0 text-lasso-deep" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep">Purpose</p>
                <p className="mt-2 leading-relaxed text-lasso-navy">{sop.purpose}</p>
              </div>
            </div>
          )}
          <Steps steps={sop.steps} />
          {sop.status === "published" && <AckCard sopId={id} version={sop.version} />}
          {user.role !== "maintenance_tech" && <VersionHistory sopId={id} currentVersion={sop.version} onSummaryChange={load} />}
          <RelatedForms kind="sops" id={id} />
        </div>
      </div>
    </div>
  );
}
